import dynamic from 'next/dynamic';

import { type ModuleScheduleItem, ModuleScheduleSection } from '@common/clients/api';
import { type ContextData } from '@common/defaults/ContextData';
import { ComponentErrorBoundary } from '@common/molecules/ComponentErrorBoundary';
import styles from '@web/molecules/ArticlePromoItem/ArticlePromoItem.module.scss';

import { Container } from './atoms/Container';

const DynamicArticlePromo = dynamic(() =>
    import('@web/molecules/ArticlePromoItem/atoms/ArticlePromo').then((module) => module.ArticlePromo),
);
const DynamicCommentPromo = dynamic(() =>
    import('@web/molecules/ArticlePromoItem/atoms/CommentPromo').then((module) => module.CommentPromo),
);
const DynamicHomePromo = dynamic(() =>
    import('@web/molecules/ArticlePromoItem/atoms/HomePromo').then((module) => module.HomePromo),
);

export interface Props {
    hasDarkBackground?: boolean;
    moduleScheduleItem: ModuleScheduleItem;
    contextData: ContextData;
}

export const ArticlePromoItem = ({ moduleScheduleItem, hasDarkBackground, contextData }: Props) => {
    const { url, module, bookmaker } = moduleScheduleItem;
    const modulePlacement = module || ModuleScheduleSection.PROMO_ELEMENT_BELOW_ARTICLE;
    const icon = hasDarkBackground ? bookmaker?.darkBackgroundIcon : bookmaker?.lightBackgroundIcon;

    const itemClasses = [styles.ArticlePromoItem];
    itemClasses.push(styles[`${modulePlacement}-item`]);

    const backgroundColor = bookmaker?.backgroundColor;

    return (
        <Container
            itemClasses={itemClasses}
            backgroundColor={backgroundColor}
            bookmaker={bookmaker}
            moduleScheduleItem={moduleScheduleItem}
        >
            {modulePlacement === ModuleScheduleSection.ODDS_DOSSIER_HOME ? (
                <ComponentErrorBoundary title={'boundaryError.homePromo'}>
                    <DynamicHomePromo
                        moduleScheduleItem={moduleScheduleItem}
                        url={url || ''}
                        icon={icon || ''}
                        contextData={contextData}
                    />
                </ComponentErrorBoundary>
            ) : modulePlacement === ModuleScheduleSection.COMMENTS ? (
                <ComponentErrorBoundary title={'boundaryError.commentPromo'}>
                    <DynamicCommentPromo
                        moduleScheduleItem={moduleScheduleItem}
                        url={url || ''}
                        contextData={contextData}
                        partnerLogoSrc={icon || ''}
                    />
                </ComponentErrorBoundary>
            ) : (
                <ComponentErrorBoundary title={'boundaryError.articlePromo'}>
                    <DynamicArticlePromo
                        moduleScheduleItem={moduleScheduleItem}
                        url={url || bookmaker?.url || ''}
                        icon={icon || ''}
                        contextData={contextData}
                    />
                </ComponentErrorBoundary>
            )}
        </Container>
    );
};
